import { useQuery } from '@apollo/client/react';
import { Badge, Group, Select, Stack, Table, Text, Title } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { QueryState } from '../components/QueryState';
import { StatusBadge } from '../components/StatusBadge';
import { TablePager } from '../components/TablePager';
import type { StoryMode, StoryStatus } from '../gql/graphql';
import { AdminStoriesDocument } from '../graphql/operations';
import { shortId, STORY_MODES, STORY_STATUSES, useLabels } from '../labels';

const PAGE_SIZE = 20;

export function StoriesPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const labels = useLabels();
  const [status, setStatus] = useState<StoryStatus | null>(null);
  const [mode, setMode] = useState<StoryMode | null>(null);
  const [page, setPage] = useState(1);

  const { data, loading, error } = useQuery(AdminStoriesDocument, {
    variables: {
      status,
      mode,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    },
    fetchPolicy: 'cache-and-network',
  });

  return (
    <Stack gap="md">
      <Title order={2}>{t('stories.title')}</Title>

      <Group gap="sm" align="end">
        <Select
          label={t('stories.status')}
          placeholder={t('stories.anyStatus')}
          clearable
          data={STORY_STATUSES.map((value) => ({ value, label: labels.storyStatus(value) }))}
          value={status}
          onChange={(value) => {
            setStatus(value as StoryStatus | null);
            // Page 3 of a narrower result set is usually empty, which reads as
            // "no matches" rather than as "wrong page".
            setPage(1);
          }}
          w={180}
        />
        <Select
          label={t('stories.mode')}
          placeholder={t('stories.anyMode')}
          clearable
          data={STORY_MODES.map((value) => ({ value, label: labels.storyMode(value) }))}
          value={mode}
          onChange={(value) => {
            setMode(value as StoryMode | null);
            setPage(1);
          }}
          w={180}
        />
      </Group>

      <QueryState loading={loading && !data} error={error}>
        {data && (
          <>
            <Table.ScrollContainer minWidth={760}>
              <Table highlightOnHover striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('common.id')}</Table.Th>
                    <Table.Th>{t('stories.columns.user')}</Table.Th>
                    <Table.Th>{t('stories.columns.mode')}</Table.Th>
                    <Table.Th>{t('stories.columns.status')}</Table.Th>
                    <Table.Th>{t('stories.columns.title')}</Table.Th>
                    <Table.Th>{t('stories.columns.locale')}</Table.Th>
                    <Table.Th>{t('stories.columns.createdAt')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.adminStories.items.map((story) => (
                    <Table.Tr
                      key={story.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => void navigate(`/stories/${story.id}`)}
                    >
                      <Table.Td>
                        <Badge variant="default" size="sm">
                          {shortId(story.id)}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{story.user.email ?? shortId(story.user.id)}</Table.Td>
                      <Table.Td>{labels.storyMode(story.mode)}</Table.Td>
                      <Table.Td>
                        <StatusBadge status={story.status} />
                      </Table.Td>
                      <Table.Td>
                        {story.title ?? <Text c="dimmed">{t('common.empty')}</Text>}
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="default" size="sm">
                          {story.locale.toUpperCase()}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{labels.dateTime(story.createdAt)}</Table.Td>
                    </Table.Tr>
                  ))}
                  {data.adminStories.items.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={7}>
                        <Text c="dimmed" ta="center" py="md">
                          {t('common.nothingFound')}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>

            <TablePager
              total={data.adminStories.total}
              limit={PAGE_SIZE}
              page={page}
              onChange={setPage}
            />
          </>
        )}
      </QueryState>
    </Stack>
  );
}
